import React from 'react';
import moment from 'moment';
import keyBy from 'lodash/keyBy';
import formatters from '../defaults/formatters.json';
import DateTimeWithPopover from '../components/common/DateTimeWithPopover';
import Guid from '../components/common/Guid';
import GuidCID from '../components/common/GuidCID';
import UnitId from '../components/common/UnitId';
import LongString from '../components/common/LongString';
import { store } from '../index';
import unitIdFeatures from '../defaults/unitIdFeatures.json';
import fieldDefOverrides from '../defaults/fieldDefOverrides.json';

const qs = require('qs');

export function getLayerLookup(operationalLayers) {
  // Create a dictionary to relate map layer names to webmap titles
  const lookup = {};

  if (!operationalLayers) {
    return lookup;
  }

  operationalLayers.forEach(layer => {
    lookup[layer.title] = layer;
  });

  return lookup;
}

export function getTableLookup(tables) {
  // Create a dictionary to relate map layer names to webmap titles
  const lookup = {};

  if (!tables) {
    return lookup;
  }

  tables.forEach(table => {
    lookup[table.title] = table;
  });

  return lookup;
}

export function getFieldType(fieldInfo) {
  if (fieldInfo.domain && fieldInfo.domain.type === 'coded-value') {
    return 'domain';
  }

  return fieldInfo.type || 'string';
}

export function formatAttribute(
  field,
  value,
  layerName,
  fieldsDefinition,
  getRawValue = false
) {
  let fieldInfos;

  if (layerName) {
    fieldInfos = getFieldInfos(layerName);
  }

  if (fieldsDefinition) {
    fieldInfos = arrayToLookup(fieldsDefinition, 'name');
  }

  if (!fieldInfos) {
    return { name: field, value };
  }

  const fieldInfo = fieldInfos[field];

  if (!fieldInfo) return { name: '', value: '' };

  let formattedValue;

  if (fieldInfo.domain && fieldInfo.domain.type === 'coded-value') {
    formattedValue = formatValue(
      fieldInfo.type,
      value,
      arrayToLookup(fieldInfo.domain.codedValues, 'code'),
      getRawValue
    );
  } else {
    formattedValue = formatValue(fieldInfo.type, value, null, getRawValue);
  }

  return {
    name: fieldInfo.alias,
    value: formattedValue
  };
}

export function arrayToLookup(array, key) {
  if (!array || !key) {
    return null;
  }

  return keyBy(array, key);
}

export function defineIncident(incident) {
  if (incident && !incident.relationships) {
    return <p style={{ textAlign: 'center' }}>No Relationships</p>;
  }

  if (incident && incident.relationships) {
    const complexes = [];
    const merges = [];
    const conflicts = [];
    const responses = [];
    const postfire = [];
    const prescribed = [];
    let hasComplex = false;
    let hasMerge = false;
    let hasConflict = false;
    let hasOR = false;
    let hasPostFire = false;
    let hasPrescribed = false;
    let isMergeParent = false;
    let isConflictParent = false;
    let isResponseParent = false;
    let isPostFireParent = false;
    let isPrescribedParent = false;

    incident.relationships.forEach(rel => {
      // assume related incident the parent
      const relatedIncidentId = rel.attributes.IrwinID;
      const isChild = rel.relationshipInfo.isChild;
      console.log(isChild, getCurrentRouteFragment(window.location.pathname));

      switch (rel.relationshipInfo.attributes.RelationshipType) {
        case 'Complex':
          hasComplex = true;
          complexes.push({ id: relatedIncidentId, isChild, ...rel });
          break;
        case 'Merge':
          hasMerge = true;
          merges.push({ id: relatedIncidentId, isChild, ...rel });
          if (isChild) {
            isMergeParent = true;
          }
          break;
        case 'PotentialConflict':
          hasConflict = true;
          conflicts.push({ id: relatedIncidentId, isChild, ...rel });
          if (isChild) {
            isConflictParent = true;
          }
          break;
        case 'ProvidingResponseTo':
          hasOR = true;
          responses.push({ id: relatedIncidentId, isChild, ...rel });
          if (isChild) {
            isResponseParent = true;
          }
          break;
        case 'PostFire':
          hasPostFire = true;
          postfire.push({ id: relatedIncidentId, isChild, ...rel });
          if (isChild) {
            isPostFireParent = true;
          }
          break;
        case 'PrescribedEscape':
          hasPrescribed = true;
          prescribed.push({ id: relatedIncidentId, isChild, ...rel });
          if (isChild) {
            isPrescribedParent = true;
          }
          break;
        default:
          console.error('Incident Relationship has no type.');
      }
    });

    const decoratedIncident = incident.clone();
    decoratedIncident.relationships = incident.relationships;
    decoratedIncident.derived = {
      hasComplex,
      hasMerge,
      hasConflict,
      hasOR,
      hasPostFire,
      hasPrescribed,
      complexes,
      merges,
      conflicts,
      responses,
      postfire,
      prescribed,
      isMergeParent,
      isConflictParent,
      isResponseParent,
      isPostFireParent,
      isPrescribedParent
    };

    return decoratedIncident;
  }
}

export function getCurrentRouteFragment(path) {
  const route = path.substr(path.lastIndexOf('/') + 1);
  return route;
}

export function getFilterDefs(filterDefs, fields) {
  const fieldInfos = arrayToLookup(fields, 'name');

  const filterState = filterDefs.map(def => {
    const { name, fieldNames, type, label, container } = def;

    const options = [];
    Object.keys(fieldInfos).map(key => {
      const fieldInfo = fieldInfos[key];

      if (
        fieldNames.indexOf(fieldInfo.name) > -1 &&
        fieldInfo.domain &&
        fieldInfo.domain.codedValues
      ) {
        options.push(...fieldInfo.domain.codedValues);
      }
      return true;
    });

    return {
      name,
      type,
      label,
      fieldNames,
      options,
      container,
      values: []
    };
  });

  return arrayToLookup(filterState, 'name');
}

export function getUrlParams() {
  const queryString = window.location.search;
  if (!queryString) return {};
  return qs.parse(queryString.slice(1));
}

export function applyFieldArrayOverrides(fields, overrideKey) {
  // this function modifies fields array in-place

  const overrideFieldInfos = arrayToLookup(
    fieldDefOverrides[overrideKey],
    'name'
  );

  fields.forEach(f => {
    if (overrideFieldInfos && overrideFieldInfos[f.name]) {
      f.type = overrideFieldInfos[f.name].type || f.type;
      f.alias = overrideFieldInfos[f.name].alias || f.alias;
    }
  });

  return fields;
}

export function applyFeatArrayOverrides(feature, overrideKey) {
  // this function modifies fields array in-place

  const overrideFieldInfos = arrayToLookup(
    fieldDefOverrides[overrideKey],
    'name'
  );

 feature.forEach(f => {
    if (overrideFieldInfos && overrideFieldInfos[f.name]) {
      f.type = overrideFieldInfos[f.name].type || f.type;
      f.alias = overrideFieldInfos[f.name].alias || f.alias;
    }
  });

  return feature;
}

export function applyFieldObjectOverrides(fields, overrideKey) {
  // this function modifies fields object in-place

  const overrideFieldInfos = arrayToLookup(
    fieldDefOverrides[overrideKey],
    'name'
  );

  Object.keys(fields).forEach(key => {
    const f = fields[key];
    if (overrideFieldInfos && overrideFieldInfos[f.name]) {
      f.type = overrideFieldInfos[f.name].type || f.type;
      f.alias = overrideFieldInfos[f.name].alias || f.alias;
    }
  });

  return fields;
}

export function alphaSort(key) {
  return (a, b) => {
    if (a[key] < b[key]) return -1;
    if (a[key] > b[key]) return 1;
    return 0;
  };
}

//
// Private Methods
//

function formatValue(type, value, codedValues, getRawValue) {
  // null value
  if (value === null) {
    if (getRawValue) {
      return '';
    }
    return <div className="no-value">{'\u2014'}</div>;
  }

  // field has a domain - pull coded value
  if (codedValues) {
    return codedValues[value] ? codedValues[value].name : value;
  }

  switch (type) {
    case 'birthMonthDay':
      if (value.length === 4) {
        return `${value.slice(0, 2)}/${value.slice(2, 4)}`;
      }

      return '';
    case 'small-integer':
    case 'integer':
    case 'single':
    case 'double':
    case 'long':
      return Number(value).toLocaleString();
    case 'date':
    case 'esriFieldTypeDate':
      if (getRawValue) {
        return moment(value).format(formatters.dateTimeFormat);
      }
      return (
        <DateTimeWithPopover
          datetime={value}
          format={formatters.dateTimeFormat}
        />
      );

    case 'guid':
      if (getRawValue) return value;
      return <Guid>{value}</Guid>;

    case 'guidCID':
      if (getRawValue) return value;
      return <GuidCID>{value}</GuidCID>;

    // POO Latitude + POO Longitude
    case 'latlong':
      if (getRawValue) return value;
      return (
        <span title={value}>
          {Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 6,
            maximumFractionDigits: 6
          })}
        </span>
      );
    case 'unit-id':
      if (getRawValue) return value;

      const unitIdFeature = unitIdFeatures.units.find(feature =>
        feature.UNITID.includes(value && value.toUpperCase())
      );

      const unitIdAttributes =
        unitIdFeature || null;

      return <UnitId value={value} unitIdAttributes={unitIdAttributes} />;
    case 'string':
      // if the string length is greater than 50 char, format the string into a LongString component, just return value if we need the raw value for CSV
      if (value !== undefined && (value.length >= 50 && !getRawValue) ) {
        return <LongString text={value} />;
      }
      return value;
    case 'esriFieldTypeGlobalID':
      return null;

    default:
      return value;
  }
}

function getFieldInfos(layerName) {
  switch (layerName) {
    case 'incident':
      return store.getState().incident.fields;
    case 'incidentHistory':
      return store.getState().incident.historyFields;
    case 'incident209Resources':
      return store.getState().incident.resource209Fields;
    case 'resource':
      return store.getState().resource.fields;
    default:
      return null;
  }
}



// WEBPACK FOOTER //
// ./src/utils/helpers.js